import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useNotificationsLastSeen } from "@mirage/service-dash-trial/hooks/useNotificationsLastSeen";
import { hideSnackbar } from "@mirage/shared/snackbar";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { trialEndingNotificationAtom } from "./atoms";
import { TrialActionType } from "./types";
import { getActionSurface, useLogShownUpsellComponent } from "./utils";

const TRIGGER_STACK_UPSELL_AFTER_DAYS_LEFT = 15;
const DAY_COOLDOWN = 1; // we only want to show the stacks upsell once a day

export const useStackTrialUpsell = () => {
  const [trialEndingNotification, setTrialEndingNotification] = useAtom(
    trialEndingNotificationAtom,
  );

  const logShownUpsell = useLogShownUpsellComponent();
  const isAdmin = useIsAdminUser();

  const setStackContainerRef = useCallback(
    (ref: React.RefObject<HTMLElement> | null) => {
      setTrialEndingNotification((prev) => ({
        ...prev,
        stackContainerRef: ref,
      }));
    },
    [setTrialEndingNotification],
  );

  const { setLastSeen, lastSeenStacksNotification } =
    useNotificationsLastSeen();
  const { daysLeft } = trialEndingNotification;

  const showStackTrialNotification = useCallback(
    (daysLeft: number) => {
      setTrialEndingNotification((prev) => ({
        ...prev,
        actionType: TrialActionType.Stacks,
        isVisible: true,
        containerRef: prev.stackContainerRef,
      }));
      // the copy to clipboard snackbar will always show on-top of the upsell unless we manually hide it
      hideSnackbar();
      setLastSeen({ stacks: daysLeft });
      logShownUpsell({
        actionSurface: getActionSurface(TrialActionType.Stacks),
        ctaName: isAdmin ? "view_plans" : "ask_your_admin",
      });
    },
    [setTrialEndingNotification, setLastSeen, logShownUpsell],
  );

  const stackTriggerTrialUpsellCheck = useCallback(() => {
    if (
      daysLeft <= TRIGGER_STACK_UPSELL_AFTER_DAYS_LEFT &&
      daysLeft <= lastSeenStacksNotification - DAY_COOLDOWN
    ) {
      showStackTrialNotification(daysLeft);
    }
  }, [daysLeft, lastSeenStacksNotification]);

  const isTrialNotificationVisible = trialEndingNotification.isVisible;

  const hideTrialNotification = useCallback(() => {
    setTrialEndingNotification((prev) => ({
      ...prev,
      isVisible: false,
    }));
  }, [setTrialEndingNotification]);

  return {
    hideTrialNotification,
    isTrialNotificationVisible,
    stackTriggerTrialUpsellCheck,
    setStackContainerRef,
  };
};
